if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.productBar = this.closest('[data-rc-product-quick-view-content]')
          ? null : document.querySelector('product-bar');
        this.productBarSubmit =
          this.productBar?.querySelectorAll('[data-submit]');
        this.form = this.querySelector('form');

        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));

        if (this.hasAttribute('data-gifts')) {
          this.gifts = {
            addedProducts: [
              ...JSON.parse(this.getAttribute('data-gifts')).map((id) => ({
                id,
              })),
            ],
          };
        } else {
          this.gifts = this.closest('.product')?.querySelector(
            'subscription-upsells',
          );
        }

        this.cart =
          document.querySelector('cart-notification') ||
          document.querySelector('cart-drawer');
        this.submitButton = this.querySelectorAll('[type="submit"]');
        this.submitButtonText = this.querySelectorAll('[type="submit"] span');

        if (document.querySelector('cart-drawer'))
          this.submitButton.forEach((button) =>
            button.setAttribute('aria-haspopup', 'dialog'),
          );

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      async onSubmitHandler(evt) {
        evt.preventDefault();
        evt.stopImmediatePropagation();
        evt.stopPropagation();

        const rechargeView = this.closest('[data-rc-product-quick-view-content]');
        if (rechargeView) {
          await rechargeView.rcQuickView?.submit(this.form);
          return;
        }

        this.cart =
          this.cart ||
          document.querySelector('cart-notification') ||
          document.querySelector('cart-drawer');

        if (this.variantIdInput.dataset.name) {
          this.variantIdInput.name = this.variantIdInput.dataset.name;
        }

        if (this.submitButton[0].getAttribute('aria-disabled') === 'true')
          return;

        this.handleErrorMessage();
        this.setLoadingState();

        const { hasSubscription, baseItemHasSellingPlan } =
          this.getSellingPlanState();
        const relatedSellingPlanKey = baseItemHasSellingPlan
          ? String(this.form.elements.selling_plan.value)
          : 'one_time';
        this.resolveGiftsForSubscription(hasSubscription);

        const config = this.gifts ? fetchConfig() : fetchConfig('javascript');
        if (!this.gifts) {
          config.headers['X-Requested-With'] = 'XMLHttpRequest';
          delete config.headers['Content-Type'];
        }

        const formData = this.buildFormData();
        const sectionsUrl = window.location.pathname.startsWith('/tools/') ? '/' : window.location.pathname;

        const addProductWithRelatedItems = async () => {
          let eligibilityChecked = false;
          const { productsToRemove, giftItemsToAdd } =
            await this.resolveCartGiftState(relatedSellingPlanKey);
          await this.removeStaleGiftProducts(productsToRemove);

          formData.append('sections_url', sectionsUrl);
          config.body = this.buildCartAddBody(
            formData,
            giftItemsToAdd,
            baseItemHasSellingPlan,
          );

          const response = await fetch(`${routes.cart_add_url}`, config);
          let responseData = await response.json();

          try {
            if (response.ok && typeof CartItems !== 'undefined') {
              const cartItems =
                this.cart?.tagName === 'CART-DRAWER'
                  ? this.cart.querySelector('cart-drawer-items') ||
                    document.querySelector('cart-drawer-items')
                  : null;
              const reconciliationResult =
                await CartItems.reconcileCartRelationships(
                  [
                    {
                      variantId: Number(this.form.elements.id.value),
                      gifts: JSON.parse(
                        this.getAttribute('data-gifts') || '[]',
                      ),
                      paidProducts: JSON.parse(
                        this.getAttribute('data-paid-products') || '[]',
                      ),
                    },
                  ],
                  {
                    renderContext: this.cart
                      ? {
                          sections: this.cart
                            .getSectionsToRender()
                            .map((section) => section.id),
                          sections_url: sectionsUrl,
                        }
                      : {},
                  },
                );
              if (
                reconciliationResult.didMutate &&
                reconciliationResult.renderState?.sections
              ) {
                const normalizedState = reconciliationResult.renderState;
                normalizedState.id = responseData.id;
                normalizedState.key = responseData.key;
                responseData = normalizedState;
              }

              if (cartItems) {
                const eligibilityState =
                  await cartItems.checkFreeGiftEligibility(
                    reconciliationResult.cartState,
                  );
                eligibilityChecked = true;
                if (eligibilityState?.sections) {
                  eligibilityState.id = responseData.id;
                  eligibilityState.key = responseData.key;
                  responseData = eligibilityState;
                }
              }
            }
          } catch (normalizationError) {
            console.error(
              '[ProductForm] Cart normalization failed:',
              normalizationError,
            );
          }

          this.handleAddToCartResponse(responseData, formData, {
            eligibilityChecked,
          });
        };

        const usesCartQueue = typeof CartItems !== 'undefined';
        if (usesCartQueue && !CartItems.lockCartInteractions()) {
          this.resetLoadingState();
          return;
        }

        try {
          if (usesCartQueue) {
            await CartItems.enqueueMutation(addProductWithRelatedItems);
          } else {
            await addProductWithRelatedItems();
          }
        } catch (error) {
          console.error(error);
        } finally {
          this.resetLoadingState();
          if (usesCartQueue) CartItems.unlockCartInteractions();
        }
      }

      getSellingPlanState() {
        const sellingPlan = this.form.elements.selling_plan;
        const isCheckbox =
          (sellingPlan && sellingPlan.type === 'checkbox') || false;
        const hasSubscription =
          sellingPlan?.value !== '' && (!isCheckbox || sellingPlan?.checked);
        const baseItemHasSellingPlan =
          (sellingPlan?.value !== '' && sellingPlan?.type !== 'checkbox') ||
          (sellingPlan?.value !== '' &&
            sellingPlan?.type === 'checkbox' &&
            sellingPlan?.checked);
        return { hasSubscription, baseItemHasSellingPlan };
      }

      resolveGiftsForSubscription(hasSubscription) {
        const attributeName = hasSubscription
          ? 'data-gifts'
          : 'data-paid-products';
        if (this.hasAttribute(attributeName)) {
          this.gifts = {
            addedProducts: [
              ...JSON.parse(this.getAttribute(attributeName)).map((id) => ({
                id,
              })),
            ],
          };
          return;
        }
      }

      buildFormData() {
        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id),
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        return formData;
      }

      async resolveCartGiftState(relatedSellingPlanKey) {
        const removeProductsValue = this.getAttribute('data-remove-products');
        const shouldLoadCart = Boolean(removeProductsValue);
        const cartData = shouldLoadCart
          ? await fetch(`${routes.cart_url}`, { ...fetchConfig() }).then(
              (response) => response.json(),
            )
          : { items: [] };
        const productsToRemove = [];
        if (removeProductsValue) {
          const idsArray = removeProductsValue
            .split(',')
            .map((id) => Number(id));
          idsArray.length > 0 &&
            productsToRemove.push(
              ...cartData.items.filter((item) =>
                idsArray.includes(item.variant_id),
              ),
            );
        }
        const giftItemsToAdd = this.gifts?.addedProducts.map((gift) => ({
          id: Number(gift.id),
          quantity: 1,
          properties: {
            _gift_for: Number(this.form.elements.id.value),
            _gift_for_selling_plan: relatedSellingPlanKey,
          },
        }));
        return { productsToRemove, giftItemsToAdd };
      }

      async removeStaleGiftProducts(productsToRemove) {
        if (productsToRemove.length === 0) return;
        const response = await fetch(`${routes.cart_update_url}`, {
          ...fetchConfig(),
          ...{
            body: JSON.stringify({
              updates: productsToRemove.reduce((acc, val) => {
                return {
                  ...acc,
                  [val.key]: 0,
                };
              }, {}),
            }),
          },
        });

        if (!response.ok) {
          throw new Error('Failed removing stale related products');
        }
      }

      buildCartAddBody(formData, giftItemsToAdd, baseItemHasSellingPlan) {
        if (!this.gifts) return formData;
        return JSON.stringify({
          items: [
            {
              id: Number(this.form.elements.id.value),
              ...(baseItemHasSellingPlan && {
                selling_plan: Number(this.form.elements.selling_plan.value),
              }),
              quantity: 1,
              // This used to be included for alt product views, no longer used
              // properties: {
              //   _alternate_url: window.location.pathname,
              // },
            },
            ...giftItemsToAdd,
          ],
          sections: this.cart
            ? this.cart.getSectionsToRender().map((section) => section.id)
            : [],
          sections_url: window.location.pathname,
        });
      }

      handleAddToCartResponse(
        response,
        formData,
        { eligibilityChecked = false } = {},
      ) {
        if (response.status) {
          publish(PUB_SUB_EVENTS.cartError, {
            source: 'product-form',
            productVariantId: formData.get('id'),
            errors: response.errors || response.description,
            message: response.message,
          });
          this.handleErrorMessage(response.description);

          const soldOutMessage =
            this.submitButton[0].querySelector('.sold-out-message');
          if (!soldOutMessage) return;
          this.submitButton.forEach((button) =>
            button.setAttribute('aria-disabled', true),
          );
          this.submitButtonText.forEach((text) => text.classList.add('hidden'));
          soldOutMessage.classList.remove('hidden');
          this.error = true;
          return;
        } else if (!this.cart) {
          window.location.href = window.routes?.cart_url || '/cart';
          return;
        }

        if (!this.error) {
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'product-form',
            productVariantId: formData.get('id'),
            cartData: response,
            sectionsWillRender: Boolean(response.sections),
            skipEligibilityCheck: eligibilityChecked,
          });

          const selectedSubscription =
            this.closest('product-info')
              ?.querySelector('product-subscriptions')
              ?.getSelectedOption()?.value || '';

          const isStarterKit = this.closest('product-info')?.querySelector('.product__title h1')?.textContent?.trim() === 'Fresh Start Kit';
          const animateClass = isStarterKit ? 'animate-quarterly-upsell-index-4' : 'animate-quarterly-upsell-index-1';

          if (selectedSubscription === 'quarterly' || selectedSubscription === 'monthly') {
            document.body.classList.add(animateClass);
            setTimeout(() => {
              document.body.classList.remove(animateClass);
            }, 5000);
          }

          if (this.hasAttribute('data-cross-sell-form')) {
            window.igEvents = window.igEvents || [];
            window.igEvents.push({ event: 'crossSellAdded' });
          }

          if (this.hasAttribute('data-cart-cross-sell-form')) {
            window.igEvents = window.igEvents || [];
            window.igEvents.push({ event: 'cartCrossSellAdded' });
          }
        }
        this.error = false;
        const quickAddModal = this.closest('quick-add-modal');
        if (quickAddModal) {
          document.body.addEventListener(
            'modalClosed',
            () => {
              setTimeout(() => {
                this.handleAddToCartRender(response, formData);
              });
            },
            { once: true },
          );
          quickAddModal.hide(true);
        } else {
          this.handleAddToCartRender(response, formData);
        }
      }

      handleAddToCartRender(response, formData) {
        this.cart.renderContents(response);

        if (this.hasAttribute('data-cart-kit')) {
          setTimeout(() => {
            window.fireCartConfetti();
          }, 100);
          return;
        }
      }

      setLoadingState() {
        this.submitButton.forEach((button) =>
          button.setAttribute('aria-disabled', true),
        );
        this.submitButton.forEach((button) => button.classList.add('loading'));
        this.querySelectorAll('.loading__spinner').forEach((spinner) =>
          spinner.classList.remove('hidden'),
        );
        this.productBarSubmit?.forEach((submit) => {
          submit.setAttribute('aria-disabled', true);
          submit.classList.add('loading');
        });
      }

      resetLoadingState() {
        this.submitButton.forEach((button) =>
          button.classList.remove('loading'),
        );
        if (this.cart && this.cart.classList.contains('is-empty'))
          this.cart.classList.remove('is-empty');
        if (!this.error)
          this.submitButton.forEach((button) =>
            button.removeAttribute('aria-disabled'),
          );
        this.querySelectorAll('.loading__spinner').forEach((spinner) =>
          spinner.classList.add('hidden'),
        );
        this.productBarSubmit?.forEach((submit) => {
          submit.removeAttribute('aria-disabled');
          submit.classList.remove('loading');
        });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper ||
          this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage =
          this.errorMessage ||
          this.errorMessageWrapper.querySelector(
            '.product-form__error-message',
          );

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.forEach((button) =>
            button.setAttribute('disabled', 'disabled'),
          );
          if (text)
            this.submitButtonText.forEach((text) => (text.textContent = text));
        } else {
          this.submitButton.forEach((button) =>
            button.removeAttribute('disabled'),
          );
          this.submitButtonText.forEach(
            (text) => (text.textContent = window.variantStrings.addToCart),
          );
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[data-name="id"], [name=id]');
      }
    },
  );
}
